import React, {
  useEffect,
  useState,
  useContext,
  useMemo,
  useCallback,
} from 'react';
import { useTranslation, Trans } from 'react-i18next';
import {
  HeaderLogo,
  ContactUsLink,
  HamburgerMenuLink,
  FooterDisclaimer,
  FooterNav,
} from 'organisms';
import { useNavigate } from 'react-router-dom';
import { getFeatureAttribute, MyFeatureAttributeResponse } from 'pages';
import {
  getMaxLength,
  ProfileUpdate,
  UserDetails,
  HasNumberGlobal,
  setEncryptedValue,
  useAuth,
  NavigateConst,
  setLocalStorageBoolean,
  getLocalStorageBoolean,
  deleteEncryptedValue,
  apiResponseSafeCheck,
  getDecryptedValue,
  LoginAuthToken,
  setSessionStorageBoolean,
  SHOW_PREFERENCE_POPUP,
  DOMAIN_SHP,
  DOMAIN_AVMED,
  userLockedOut,
  handleKeyDownUsername,
  getAnalyticsBaseConfig,
  DeviceTypes,
  useDeviceType,
  identifyUser,
  ANALYTICS_MESSAGES,
  SCREEN_NAMES,
  getErrorTypeByCode,
  domainForSegment,
  safeJsonParse,
} from 'common';
import { identifyOpenReplayUser, tagOpenReplayMemberId } from 'common/openreplay/openreplay';
import { stopPreLoginIdleMonitoring } from 'common/utils/preLoginIdleMonitor';
import LoginBanner from '../../assets/images/login_banner_img_4x.webp';
import LoginBannerAvMed from '../../assets/images/avmed-login-people.webp';
import LoginFormerAvMedMember from '../../assets/images/avmed-login-people.png';
import {
  useUserLogin,
  useFeaturePermission,
  useMyFeatureAttribute,
  IUserLoginResponse,
  Person,
} from '@sentara-health/sentara-api-hooks-core';
import { useForm } from 'react-hook-form';
import {
  Button,
  Label,
  ErrorMessage,
  Headings,
  InputField,
  GlobalError,
  EyeIconClose,
  EyeIconOpen,
  Loader,
  Link,
  Paragraph,
  ModalMultiUse,
} from '@sentara-health/optimahealth_web';
import { GlobalContext, isAvMed } from 'context';
import MaintenanceBanner from '../maintenance/MaintenanceBanner';
import LanguageDropdown from 'organisms/languageDropdown/LanguageDropdown';
import InfoBanner from '../maintenance/InfoBanner';
import { useAdobeAnalytics } from '@sentara-health/platform-analytics';

// Define the main functional component for the sign-in form
const SignInForm = () => {
  // State variables
  const {
    setLoginMemberId,
    setLoginMID,
    setLoginProxyId,
    setUserEncodedData,
    setAge,
    loginMID,
    setSSOLink,
    setProxyUniqueId,
    proxyUniqueId,
    setAuthToken,
    getAuthToken,
    setXauthToken,
    setXsentaraTokenID,
    getFeature,
    ssoEndPoints,
    logging,
     setUserTelecom,
  } = useContext(GlobalContext);
  const { t } = useTranslation();
  const deviceType = useDeviceType();
  const isMobile = deviceType === DeviceTypes.MOBILE;
  const isTablet = deviceType === DeviceTypes.TABLET;
  const [errorMessage, setErrorMessage] = useState<boolean>(false);
  const [guest, setGuest] = useState<boolean>(false);
  const [isWellnessUser, setWellnessUser] = useState<boolean>(false);
  const [errorStatusMessage, setErrorStatusMessage] = useState<boolean>(false);
  const [internalServerError, setInternalServerError] =
    useState<boolean>(false);
  const [passwordOpen, setPasswordOpen] = useState<boolean>(false);
  const inActiveSignOut: boolean = getLocalStorageBoolean(
    UserDetails?.inActiveSignOut
  );
  const [isOpen, setIsOpen] = useState<boolean>(inActiveSignOut);

  // Fetch user state and login function from API hooks
  const { userLogin } = useUserLogin();
  const { getFeaturePermission } = useFeaturePermission();
  const { MyFeatureAttributeType } = useMyFeatureAttribute();
  const [loading, setLoading] = useState(false);

  // Show or hide account locked popup
  const [showPopup, setShowPopup] = useState<boolean>(false);

  // True when any sign-in error banner is visible
  const showGlobalError: boolean =
    errorMessage || errorStatusMessage || internalServerError;

  const globalErrorVariant = isMobile
    ? isAvMed() && getFeature?.FormerAvmedMembers
      ? 'mb-3 mt-2'
      : 'mb-3 mt-3'
    : 'mb-3 mt-n4 mt-3';

  // Form validation using React Hook Form
  const {
    register,
    handleSubmit,
    watch,
    reset,
    formState: { errors, isValid },
  } = useForm({
    defaultValues: {
      memberId: '',
      password: '',
    },
  });

  // Watch for changes in username and password inputs
  const watchUsername = watch('memberId');
  const watchPassword = watch('password');

  // Determine which AvMed logo to use based on feature flag
  const avMedLogin = getFeature?.FormerAvmedMembers
    ? LoginFormerAvMedMember
    : LoginBannerAvMed;
  // Choose between AvMed logo or standard login banner
  const imageLogin = isAvMed() ? avMedLogin : LoginBanner;

  // Determine the CSS class for the banner column
  const getBannerColumnClass = () => {
    if (getFeature?.FormerAvmedMembers) {
      return 'col-4 order-3 d-none d-xl-flex justify-content-end align-items-start align-self-start';
    }

    const colSizeClass = isAvMed() ? 'col-lg-6' : 'col-lg-7';
    return `d-none d-xl-flex ps-4 pe-0 ${colSizeClass} justify-content-end align-items-start align-self-start`;
  };

  const loginImage = () => {
    let loginImageClass = 'shp-login-img';
    if (isAvMed()) {
      const baseImageClass = getFeature?.FormerAvmedMembers
        ? 'former-member-login-img'
        : 'avmed-login-img';
      const errorModifierClass = showGlobalError
        ? getFeature?.FormerAvmedMembers
          ? 'has-former-global-error'
          : 'has-global-error'
        : '';

      loginImageClass = [baseImageClass, errorModifierClass]
        .filter(Boolean)
        .join(' ');
    }
    return (
      <img
        data-testid="signInBannerImage"
        src={imageLogin}
        className={`${loginImageClass} img-fluid`}
        alt=""
        aria-hidden="true"
      />
    );
  };

  const clearLocalStorage = useCallback((preserveInactiveSignOut = false) => {
    const savedLanguage = localStorage.getItem('userLanguagePreference');
    const shouldPreserveInactiveSignOut =
      preserveInactiveSignOut &&
      getLocalStorageBoolean(UserDetails?.inActiveSignOut);

    localStorage.clear();

    if (savedLanguage) {
      localStorage.setItem('userLanguagePreference', savedLanguage);
    }

    if (shouldPreserveInactiveSignOut) {
      setLocalStorageBoolean(UserDetails?.inActiveSignOut, true);
    }
  }, []);

  useEffect(() => {
    clearLocalStorage(true);
    setSessionStorageBoolean(SHOW_PREFERENCE_POPUP, false);
    deleteEncryptedValue();
  }, [clearLocalStorage, t]);

  useEffect(() => {
    if (!isOpen) return;
    const timeoutId = globalThis.setTimeout(() => {
      setLocalStorageBoolean(UserDetails?.inActiveSignOut, false);
    }, 30000);

    return () => {
      globalThis.clearTimeout(timeoutId);
    };
  }, [isOpen]);

  let search = window.location.search;

  // to get the EOB params from the URL
  const params = new URLSearchParams(search);
  const redirectValue: string = params.get('redirect') ?? '';

  // Reset password error message on username or password change
  useEffect(() => {
    if (redirectValue.includes('/?proxy=')) {
      const proxyId = redirectValue.split('/?proxy=');
      setProxyUniqueId(proxyId[1]);
    }
  }, [watchUsername, watchPassword, redirectValue, t, setProxyUniqueId]);
  const navigate = useNavigate();

  // Authentication context
  const auth: any = useAuth();

  // Handle error codes and display error messages

  const handleError = (errorCode: string | undefined): void => {
    clearLocalStorage();
    deleteEncryptedValue();
    const regex = HasNumberGlobal;
    const getStatusCode: string | null =
      errorCode?.match(regex)?.toString() ?? errorCode ?? '';
    const errorStatusCode: string[] = ['441', '400', '442', '445', '420'];
    const errorStatusCode2: string[] = ['401', '440'];
    const internalServerError: string[] = ['500', 'Network Error'];
    const checkError: boolean = errorStatusCode.includes(getStatusCode);
    const checkError2: boolean = errorStatusCode2.includes(getStatusCode);
    const checkServerError: boolean =
      internalServerError.includes(getStatusCode);

    if (checkError) {
      setErrorMessage(true);
    } else if (checkError2) {
      setErrorStatusMessage(true);
      return;
    } else if (checkServerError) {
      setInternalServerError(true);
      return;
    } else {
      setErrorMessage(false);
      setErrorStatusMessage(false);
      setInternalServerError(false);
    }
  };

  const setProfileData = async (
    response: {
      data: {
        rules: { profileUpdate: string };
        authToken: string;
        user: { firstName: string; lastName: string };
        bearerToken: string;
      };
    },
    user: {}
  ) => {
    if (response?.data?.rules?.profileUpdate) {
      localStorage.setItem(ProfileUpdate, response?.data?.rules?.profileUpdate);
    }
    if (response?.data?.authToken) {
      await handleAuthToken(response, setEncryptedValue, setAuthToken);
    }
    auth.login(user ? response?.data?.authToken : null);
    //setting domain cookie
    const optimaPersonCookie =
      'FirstName=' +
      response?.data?.user?.firstName +
      '&' +
      'LastName=' +
      response?.data?.user?.lastName +
      ';' +
      'domain=.optimahealth.com;path=/;';
    document.cookie = UserDetails.optimaPersonCookie + '=' + optimaPersonCookie;
    document.cookie =
      UserDetails.optimaRoleCookie +
      '=' +
      'Role=members;domain=.optimahealth.com;path=/';
  };

  // // Handle form submission
  const onSubmit = async (data: { memberId: string; password?: string }) => {
    setInternalServerError(false);
    const user: any = {
      userId: data.memberId ?? '',
      password: data.password ?? '',
      channel: 'Web',
      lang: 'en',
      identification: {
        deviceId: 'string',
      },
    };

    setLoading(true);
    const response: any = await userLogin(user);
    const x_forgerock_sso = response?.headers?.['x-sentara-forgerocksso'];

    if (x_forgerock_sso) {
      document.cookie = `${x_forgerock_sso}; Domain=${
        isAvMed() ? DOMAIN_AVMED : DOMAIN_SHP
      }; Path=/;`;
    }
    await setEncryptedValue(UserDetails.usedId, data?.memberId);
    await setEncryptedValue(UserDetails.guest, JSON.stringify(response?.data));
    await setEncryptedValue(
      UserDetails.id,
      JSON.stringify(response?.data?.user?.id) ?? ''
    );
    setGuest(response?.data?.isGuest);
    setWellnessUser(response?.data?.isWellnessUser);
    if (response?.error) {
      handleError(response?.error); // Assuming this function handles displaying the error to the user
      setLoading(false);

      const apiDetails = {
        exception: {
          errorType: getErrorTypeByCode(response?.errorCode?.toString()),
          message: response?.errorDetails,
          stack: response?.error,
        },
        response: { httpStatus: response?.errorCode },
      };
      logging?.error({
        additionalDetails: getAnalyticsBaseConfig({
          userId: '',
        }),
        apiDetails: apiDetails,
        message: ANALYTICS_MESSAGES.AUTH_SIGNIN_EXCEPTION,
        screenName: SCREEN_NAMES.SIGN_IN,
      });
      contentListingItemClicked({
        segment: `${domainForSegment}${window.location.pathname}`,
        contentListing: {
          eventType: 'Content Listing Item Clicked',
          listing: {
            content: [
              {
                contentId: '507',
                contentTitle: ANALYTICS_MESSAGES.SIGN_IN_SELECT_FAILURE,
                itemPosition: 6,
              },
            ],
          },
        },
      });
    } else {
      logging?.trace({
        additionalDetails: getAnalyticsBaseConfig({
          userId: response?.data?.user?.id ?? '',
        }),
        message: ANALYTICS_MESSAGES.AUTH_SIGNIN_SUCCESS,
        screenName: SCREEN_NAMES.SIGN_IN,
      });
      contentListingItemClicked({
        segment: `${domainForSegment}${window.location.pathname}`,
        contentListing: {
          eventType: 'Content Listing Item Clicked',
          listing: {
            content: [
              {
                contentId: '501',
                contentTitle: ANALYTICS_MESSAGES.SIGN_IN_SELECT_SUCCESS,
                itemPosition: 1,
              },
            ],
          },
        },
      });
    }

    if (
      response?.error === t('signIn.error_419') ||
      response?.errorDetails === userLockedOut
    ) {
      setShowPopup(true);
    }

    if (!response?.data?.isGuest) {
      await setProfileData(response, user);
    } else {
      auth.login(response?.data?.authToken ?? null);
      if (response?.data?.authToken) {
        await handleAuthToken(response, setEncryptedValue, setAuthToken);
      }
    }

    identifyUser(response?.data?.user?.id);

    // Persist personId (read by the pre-login idle monitor) and hand the member's
    // identity to OpenReplay. Every field is already on the login response.
    const personId = response?.data?.user?.id ?? '';
    if (personId) {
      await setEncryptedValue(UserDetails.personId, personId);
      stopPreLoginIdleMonitoring();
      await identifyOpenReplayUser({
        userName: data.memberId,
        id: personId,
        firstName: response?.data?.user?.firstName,
        lastName: response?.data?.user?.lastName,
        dob: response?.data?.user?.dob,
      });
    } else if (response?.error && data.memberId) {
      // Failed login has no personId: tag the attempted username for traceability
      await identifyOpenReplayUser({ userName: data.memberId });
    }

    // Store telecom for proxy authorization
    if (response?.data?.telecom) {
      setUserTelecom(response.data.telecom);
    }

    setXauthToken(
      response?.data && hasXauthToken(response.data)
        ? response.data.xAuthToken
        : ''
    );
    setXsentaraTokenID(
      response?.data && hasXsentaraTokenID(response.data)
        ? response.data.xSentaraTokenID
        : ''
    );
  };

  const hasXauthToken = (
    data: IUserLoginResponse | Person
  ): data is IUserLoginResponse => 'xAuthToken' in data;

  /**
   * xSentaraTokenID - Sentara token ID required for token exchnage API for legacy API authorization using CIAM auth
   */
  const hasXsentaraTokenID = (
    data: IUserLoginResponse | Person
  ): data is IUserLoginResponse => 'xSentaraTokenID' in data;

  const handleAuthToken = async (
    response: LoginAuthToken,
    setEncryptedValue: (key: string, value: string) => Promise<void>,
    setAuthToken: (token: string) => void
  ) => {
    if (response?.data?.authToken) {
      await setEncryptedValue(UserDetails.authToken, response.data.authToken);
      await setEncryptedValue(
        UserDetails.bearerToken,
        response.data.bearerToken ?? ''
      );
      setAuthToken(response.data.authToken);
    }
  };

  //featurePermission api to navigate to the dashboard screen
  useEffect(() => {
    (async () => {
      if (getAuthToken) {
        const authToken = await getDecryptedValue(UserDetails.authToken);
        if (apiResponseSafeCheck(authToken)) {
          const response = await getFeaturePermission();
          if (response?.error) {
            setLoading(false);
            handleError(response?.error); // This function handles displaying the error message to the user
          } else {
            const decodedMemberId = atob(response?.data?.memberId ?? '');
            setLoginMID(response?.data?.mId ?? '');
            setLoginMemberId(decodedMemberId);
            setLoginProxyId(response?.data?.proxyId ?? '');
            void tagOpenReplayMemberId(decodedMemberId);
            getRedirectPath();
          }
        }
      }
    })();
  }, [getAuthToken]);

  // Redirect to dashboard or Enroll in Benefir screen based on guest user
  const getRedirectPath = () => {
    if (guest && !isWellnessUser) {
      navigate(NavigateConst.enrollBenefits);
      setLoading(false);
      return;
    } else if (isWellnessUser) {
      navigate(NavigateConst.dashboard);
      return;
    }
    getLaunchDarkly();
  };

  //getLaunchDarkly api to navigate to the dashboard screen
  const getLaunchDarkly = async () => {
    const response = await MyFeatureAttributeType();
    if (response?.errorCode !== undefined) {
      setInternalServerError(true);
    } else {
      const personId = String(
        safeJsonParse(await getDecryptedValue(UserDetails.id), '')
      );
      setUserEncodedData(
        getFeatureAttribute(
          response?.data as MyFeatureAttributeResponse,
          personId
        )
      );
      setAge(response?.data?.age);
      // to navigate to the medical claims screen if it is EOB link
      if (redirectValue && !proxyUniqueId) {
        if (redirectValue.includes(NavigateConst.claimsSummary)) {
          navigate(NavigateConst.medicalClaimsURL);
        } else if (redirectValue.includes('ssolanding')) {
          setSSOLink(redirectValue);
          navigate(NavigateConst.dashboard);
        } else if (redirectValue === 'fitnessDiscounts' && !isAvMed()) {
          const fitnessUrl = ssoEndPoints?.fitnessURL ?? '';
          if (fitnessUrl) {
            const redirectLink = document.createElement('a');
            redirectLink.href = fitnessUrl;
            redirectLink.referrerPolicy = 'origin-when-cross-origin';
            redirectLink.rel = 'noopener';
            redirectLink.target = '_self';
            redirectLink.style.display = 'none';
            document.body.appendChild(redirectLink);
            redirectLink.click();
            redirectLink.remove();
            return;
          }
          navigate(NavigateConst.dashboard);
          return;
        } else if (redirectValue.startsWith('/')) {
          navigate(redirectValue);
        } else {
          navigate(NavigateConst.dashboard);
        }
      } else {
        navigate(NavigateConst.dashboard);
      }
    }
    setLoading(false);
  };

  // SHP and AvMed Horizontal line separating the form from the register link
  const renderHorizontalLine = () => {
    let className = 'mb-3';
    if (isMobile && isAvMed()) {
      className += ' signin-border';
    } else if (!isMobile && !isAvMed()) {
      // No additional class needed
    } else {
      className += ' d-none';
    }
    return <hr className={className} />;
  };

  const { contentListingItemClicked } = useAdobeAnalytics();

  // Reset the form fields and error messages
  const resetForm = useCallback(() => {
    reset({ memberId: '', password: '' });
    setErrorMessage(false);
    setErrorStatusMessage(false);
    setInternalServerError(false);
    setShowPopup(false);
  }, [reset]);

  // Close the popup and clear localStorage
  const popupClose = useCallback(() => {
    clearLocalStorage();
    setLocalStorageBoolean(UserDetails?.inActiveSignOut, false);
    deleteEncryptedValue();
    setIsOpen(false);
    contentListingItemClicked({
      segment: `${domainForSegment}/${globalThis.location.pathname}`,
      contentListing: {
        eventType: 'Content Listing Item Clicked',
        listing: {
          content: [
            {
              contentId: ANALYTICS_MESSAGES.POP_LOGGED_OUT_DUE_TO_INACTIVITY,
              contentTitle: 'Logged Out Due to Inactivity',
              itemPosition: 1,
            },
          ],
        },
      },
    });
  }, [clearLocalStorage, contentListingItemClicked]);

  //modals object
  const Modals = useMemo(
    () => ({
      signOutModal: {
        id: 'signOutModal',
        title: t('signIn.signedOutModalTitle'),
        headerClass: 'justify-content-start',
        btnClose: false,
        content: t('signIn.signedOutModalDescription'),
        bodyClass: 'text-start',
        footer: (
          <div className="col-12 px-0">
            <Button
              dataTestId={'closeSessionButton'}
              variant="primary_btn"
              className="btn-md-height"
              onClick={popupClose}
            >
              {t('common.signIn')}
            </Button>
          </div>
        ),
        variant: 'defaultNoPyBody',
        actionOpen: () => isOpen,
        actionClose: () => setIsOpen(false),
      },
      accountLockedModal: {
        id: 'accountLockedModal',
        title: t('signIn.accountLockedError'),
        headerClass: '',
        btnClose: true,
        content: (
          <Paragraph
            level={3}
            variant="regular"
            className="lineBrake"
            data-testid="accountLockedModalDescription"
            text={
              <Trans
                i18nKey="signIn.accountLockedModalDescription"
                components={{ br: <br /> }}
              />
            }
          />
        ),
        bodyClass: '',
        footer: (
          <div className="col-12 px-0 mt-n3">
            <Button
              dataTestId="resetButton"
              variant="primary_btn"
              className="btn-md-width w-100"
              type="button"
              onClick={() => {
                setShowPopup(false);
                navigate(NavigateConst.resetPassword);
              }}
            >
              {t('pageTitle.resetPassword')}
            </Button>
            <Paragraph
              level={3}
              variant="regular"
              className="pt-3 mb-0 text-muted"
              data-testid="accountLockedModalHelp"
              text={
                t('signIn.accountLockOutFooterText')
              }
            />
          </div>
        ),
        variant: 'defaultNoPyBody',
        actionOpen: () => showPopup,
        actionClose: () => resetForm(),
      },
    }),
    [t, isOpen, showPopup, resetForm, popupClose]
  );

  // Render the sign-in form and related components
  return (
    <>
      {loading && <Loader />}
      <div className="d-flex flex-column h-100">
        {/* Header section */}
        <header className="border-bottom">
          {/* Container for the header */}
          <div className="container-fluid">
            {/* Navigation bar */}
            <nav className="header container-xl d-flex align-items-center justify-content-between">
              <HeaderLogo />
              <div className="d-flex flex-no-wrap align-items-center">
                {/* Contact us link, visible on extra-large and large screens */}
                <div className="d-none d-xl-block d-lg-block d-md-none">
                  <ContactUsLink
                    memberID={loginMID}
                    showIcon={false}
                    showFAQLink={getFeature?.FAQs}
                  />
                </div>
                {/* Hamburger menu link, visible on large, medium, and small screens */}
                <div className="d-lg-none d-md-block d-sm-block">
                  <HamburgerMenuLink showLogout={false} />
                </div>
                {getFeature?.MultiLanguage && (
                  <div className="d-none d-xl-block d-lg-block d-md-none position-relative px-4">
                    <LanguageDropdown />
                  </div>
                )}
              </div>
            </nav>
          </div>
        </header>
        {/* Main content container */}

        {/* Maintenance Banner */}
        {getFeature?.MaintenanceBannerWeb && (
          <div className="container-fluid pt-3 p-0">
            <div className={`container-xl px-0 ${isAvMed() ? '' : 'px-xl-3'}`}>
              <MaintenanceBanner />
            </div>
          </div>
        )}
        {getFeature?.InfoBannerWeb && (
          <div className="container-fluid pt-3 p-0">
            <div className={`container-xl px-0 ${isAvMed() ? '' : 'px-xl-3'}`}>
              <InfoBanner />
            </div>
          </div>
        )}

        <div
          className={`container-fluid p-0 ${
            isAvMed() && (isTablet || isMobile)
              ? `avmed-bg${showGlobalError ? ' avmed-bg-error' : ''}`
              : ''
          }`}
        >
          <div
            className={`container-xl px-main ${
              isAvMed() && !(isTablet || isMobile)
                ? `avmed-bg${showGlobalError ? ' avmed-bg-error' : ''}`
                : ''
            }`}
          >
            <div
              className={`${
                getFeature?.FormerAvmedMembers
                  ? 'row align-items-center'
                  : 'd-flex justify-content-xl-start justify-content-center align-items-center'
              }`}
            >
              {/* Left column with login form / maintenance banner */}
              <div
                className={`${
                  getFeature?.FormerAvmedMembers
                    ? 'col-xl-4 col-sm-6 col-12 px-0'
                    : 'col-xl-5 col-md-4 col-12 ps-xl-3'
                }`}
              >
                <div
                  className={`row ${
                    getFeature?.FormerAvmedMembers
                      ? 'signin-box-3c'
                      : 'ps-xl-0 pe-xl-5 px-0'
                  }`}
                >
                  {/*Maintenance banner column */}

                  {/* Login form column */}
                  <div
                    className={`col-12 ${
                      getFeature?.FormerAvmedMembers ? '' : 'px-sm-0 py-sm-5'
                    }`}
                  >
                    {/* Global errors */}
                    {(errorMessage ||
                      errorStatusMessage ||
                      internalServerError) && (
                      <GlobalError
                        variant={globalErrorVariant}
                        dataTestId="serviceError"
                      >
                        {errorMessage || errorStatusMessage
                          ? t('signIn.serviceError')
                          : t('signIn.loginServerError')}
                      </GlobalError>
                    )}
                    {/* Heading */}
                    {isMobile ? (
                      <Headings
                        level={3}
                        text={t('common.signIn')}
                        variant="semiBold"
                        dataTestId="signInHeaderText"
                        className="mb-4"
                      />
                    ) : (
                      <Headings
                        level={1}
                        text={t('common.signIn')}
                        variant="semiBold"
                        dataTestId="signInHeaderText"
                        className="mb-4"
                      />
                    )}
                    {/* User login form */}
                    <form onSubmit={handleSubmit(onSubmit)}>
                      <div className="mb-sm-3 mb-4 singin-input position-relative">
                        <div aria-hidden="true" data-focusable="false">
                          <Label className={'pb-1 d-none'} htmlFor="password">
                            {t('common.userName')}
                          </Label>
                        </div>
                        <InputField
                          data-testid="userNameInput"
                          {...register('memberId', { required: true })}
                          onKeyDown={handleKeyDownUsername}
                          id="memberId"
                          type="text"
                          placeholder={t('common.userName') ?? ''}
                          maxLength={getMaxLength.userName}
                          onFocus={() => {
                            setInternalServerError(false);
                            setErrorStatusMessage(false);
                            setErrorMessage(false);
                          }}
                          className={errors.memberId ? 'error-border' : ''}
                          aria-describedby="userid_error"
                        />
                        {errors.memberId && (
                          <div className="mt-1">
                            <ErrorMessage>
                              {t('signIn.usernameRequired')}
                            </ErrorMessage>
                          </div>
                        )}
                      </div>
                      <div className="mb-sm-2 mb-4 singin-input position-relative">
                        <div aria-hidden="true" data-focusable="false">
                          <Label className={'pb-1 d-none'} htmlFor="password">
                            {t('common.password')}
                          </Label>
                        </div>
                        <InputField
                          data-testid="passwordInput"
                          data-openreplay-masked={true}
                          {...register('password', { required: true })}
                          id="password"
                          type={passwordOpen ? 'text' : 'password'}
                          placeholder={t('common.password') ?? ''}
                          maxLength={getMaxLength.password}
                          onFocus={() => {
                            setInternalServerError(false);
                            setErrorStatusMessage(false);
                            setErrorMessage(false);
                          }}
                          className={errors.password ? 'error-border' : ''}
                          aria-describedby="password_error"
                        />
                        {errors.password && (
                          <div className="mt-1">
                            <ErrorMessage>
                              {t('signIn.passwordRequired')}
                            </ErrorMessage>
                          </div>
                        )}
                        {/* Button to toggle password visibility */}

                        <Button
                          id="eyeIconButtonSignIn"
                          variant="icon_btn"
                          dataTestId="eyeIconButton"
                          className="eye_icon"
                          onClick={(e: React.MouseEvent<HTMLElement>) => {
                            e.preventDefault();
                            setPasswordOpen(!passwordOpen);
                          }}
                        >
                          {!passwordOpen ? <EyeIconClose /> : <EyeIconOpen />}
                        </Button>
                      </div>
                      {/* reorder div for password recovery links and sign in button shp vs AvMed */}
                      <div className="d-flex flex-column">
                        <div
                          className={`mb-4 ${
                            isAvMed() ? '' : 'order-sm-1 order-2 mt-sm-0 mt-2'
                          }`}
                        >
                          <Paragraph
                            level={3}
                            text={
                              <>
                                {t('common.forgot')}
                                <Link
                                  to={NavigateConst.forgotUsername}
                                  data-testid="forgotUsernameLink"
                                  className="text-decoration-underline signIn-link px-1"
                                  aria-label="Forgot username"
                                  onClick={() =>
                                    contentListingItemClicked({
                                      segment: `${domainForSegment}${window.location.pathname}`,
                                      contentListing: {
                                        eventType:
                                          'Content Listing Item Clicked',
                                        listing: {
                                          content: [
                                            {
                                              contentId: '509',
                                              contentTitle:
                                                ANALYTICS_MESSAGES.FORGOT_USERNAME_LINK_CLICK,
                                              itemPosition: 8,
                                            },
                                          ],
                                        },
                                      },
                                    })
                                  }
                                >
                                  {t('common.userName')}
                                </Link>
                                {t('common.or')}
                                <Link
                                  to={NavigateConst.resetPassword}
                                  data-testid="forgotPasswordLink"
                                  className="text-decoration-underline signIn-link ps-1"
                                  aria-label="Forgot password"
                                  onClick={() =>
                                    contentListingItemClicked({
                                      segment: `${domainForSegment}${window.location.pathname}`,
                                      contentListing: {
                                        eventType:
                                          'Content Listing Item Clicked',
                                        listing: {
                                          content: [
                                            {
                                              contentId: '510',
                                              contentTitle:
                                                ANALYTICS_MESSAGES.FORGOT_PASSWORD_LINK_CLICK,
                                              itemPosition: 9,
                                            },
                                          ],
                                        },
                                      },
                                    })
                                  }
                                >
                                  {t('common.password')}
                                </Link>
                              </>
                            }
                            variant="regular"
                            data-testid={'sessionModalDescription'}
                            className="signInPassword pe-1"
                          />
                        </div>
                        <div
                          className={`{${
                            isAvMed() && !isMobile ? '' : 'order-sm-2 order-1'
                          }`}
                        >
                          {/* Button to submit the login form */}
                          <div className="mb-4">
                            {/* Button to submit the login form */}
                            <Button
                              type="submit"
                              dataTestId="signInButton"
                              variant="primary"
                              className={`primary_btn btn-md-width ${
                                !isValid ? 'disabled' : null
                              }`}
                            >
                              {t('common.signIn').toString()}{' '}
                            </Button>
                          </div>
                          {/* Horizontal line separator, conditionally rendered */}
                          {renderHorizontalLine()}
                          {/* "Don't have an account?" text, conditionally rendered */}
                          {isAvMed() || (!isAvMed() && !isMobile) ? (
                            <Paragraph
                              level={3}
                              text={t('signIn.notYetRegistered')}
                              variant="regular"
                              data-testid={'donthaveAccountButton'}
                              className="py-2 signInRegister"
                            />
                          ) : null}
                          {/* Button to navigate to the registration page */}
                          <Button
                            dataTestId="registerNowButton"
                            type="button"
                            onClick={() => {
                              navigate(NavigateConst.registration);
                            }}
                            variant="secondary_btn"
                            className="btn-md-width mb-4"
                          >
                            {t('signIn.registerNow')}
                          </Button>
                          {/* Link to navigate to the form to register for child-only plan */}
                          {isAvMed() && (
                            <div>
                              <Link
                                to={ssoEndPoints?.childOnlyPlanUrl}
                                target="_blank"
                                data-testid="registerChildOnlyPlanLink"
                                className="text-decoration-underline signIn-link px-1"
                              >
                                {t('signIn.registerChildOnlyPlan')}
                              </Link>
                            </div>
                          )}
                        </div>
                      </div>
                    </form>
                  </div>
                </div>
              </div>
              {/* Middle column with AvMed member information */}
              {getFeature?.FormerAvmedMembers ? (
                <div className="col-xl-4 col-sm-6 col-12 ps-sm-3">
                  <div className="row avmed-former-member-box">
                    <div className="col-12 px-0">
                      {isMobile ? (
                        <Paragraph
                          level={4}
                          text={t('maintenance.title')}
                          variant="semiBold"
                          className="mb-2"
                        />
                      ) : (
                        <Headings
                          level={5}
                          text={t('maintenance.title')}
                          variant="semiBold"
                        />
                      )}
                      <Paragraph
                        level={2}
                        text={
                          <Trans
                            i18nKey="maintenance.description"
                            components={{
                              strong: <strong />,
                            }}
                          />
                        }
                        variant="regular"
                        className="mb-sm-4 mb-3"
                      />
                      <Button
                        dataTestId="AccessFormerMemberButton"
                        type="button"
                        onClick={() => {
                          window.open(
                            ssoEndPoints?.formerAvmedMember,
                            '_blank'
                          );
                        }}
                        variant="secondary_btn"
                        className="bg-transparent"
                      >
                        {t('common.buttonText')}
                      </Button>
                    </div>
                  </div>
                </div>
              ) : null}
              {/* Right column with banner image, hidden on small screens */}
              <div className={getBannerColumnClass()}>
                {/* Determine the login image class based on conditions */}
                {loginImage()}
              </div>
            </div>
          </div>
        </div>
        {/* Footer section */}
        <footer className={`${isAvMed() ? '' : 'mt-xl-0 mt-auto'}`}>
          {/* Footer navigation and disclaimer */}
          {isAvMed() && <FooterNav />}
          <div className="container-xl">
            <FooterDisclaimer />
          </div>
        </footer>
      </div>
      {/*modals*/}
      {Modals &&
        Object.keys(Modals).map((key) => {
          const modal = Modals[key as keyof typeof Modals];
          return (
            <ModalMultiUse
              key={key}
              header={modal.title}
              headerClass={modal.headerClass}
              btnClose={modal.btnClose}
              body={modal.content}
              bodyClass={modal.bodyClass}
              footer={modal.footer}
              dataTestId={modal.id}
              variant={modal.variant}
              isOpen={modal.actionOpen()}
              onClose={modal.actionClose}
              data-testid={modal.id}
            />
          );
        })}
    </>
  );
};

export default SignInForm;
